import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import {
  HiOutlineBolt,
  HiOutlineUsers,
  HiOutlineFolder,
  HiOutlineDocument,
  HiOutlineClock,
  HiOutlineUserPlus,
  HiOutlineEnvelope,
} from 'react-icons/hi2'
import { Card } from '../../components/ui'

const TONE_CHIP = {
  brand:   'bg-brand-soft text-brand',
  success: 'bg-success-soft text-success',
  info:    'bg-info-soft text-info',
  warning: 'bg-warning-soft text-warning',
  danger:  'bg-danger-soft text-danger',
  neutral: 'bg-muted text-ink-secondary',
}

export default function QuickActions() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { getEntity } = useWorkspace()

  const actions = [
    {
      name: t('dashboard.quick.newCase', 'New {{entity}}', { entity: getEntity('case').singular }),
      description: t('dashboard.quick.newCaseDesc', 'Create a new {{entity}}', { entity: getEntity('case').singular.toLowerCase() }),
      icon: HiOutlineFolder,
      tone: 'info',
      onClick: () => navigate('/cases?action=create'),
    },
    {
      name: t('dashboard.quick.addClient', 'Add {{entity}}', { entity: getEntity('client').singular }),
      description: t('dashboard.quick.addClientDesc', 'Register a new {{entity}}', { entity: getEntity('client').singular.toLowerCase() }),
      icon: HiOutlineUsers,
      tone: 'success',
      onClick: () => navigate('/clients?action=create'),
    },
    {
      name: t('dashboard.quick.scheduleSession', 'Schedule {{entity}}', { entity: getEntity('session').singular }),
      description: t('dashboard.quick.scheduleSessionDesc', 'Plan a new {{entity}}', { entity: getEntity('session').singular.toLowerCase() }),
      icon: HiOutlineClock,
      tone: 'brand',
      onClick: () => navigate('/sessions?action=create'),
    },
    {
      name: t('dashboard.quick.uploadDocs', 'Upload {{entity}}', { entity: getEntity('document').plural }),
      description: t('dashboard.quick.uploadDocsDesc', 'Add new {{entity}}', { entity: getEntity('document').plural.toLowerCase() }),
      icon: HiOutlineDocument,
      tone: 'warning',
      onClick: () => navigate('/documents?action=upload'),
    },
    {
      name: t('dashboard.quick.invite', 'Invite {{entity}}', { entity: getEntity('employee').plural }),
      description: t('dashboard.quick.inviteDesc', 'Send email invitations'),
      icon: HiOutlineEnvelope,
      tone: 'info',
      onClick: () => navigate('/connections?action=invite'),
    },
    {
      name: t('dashboard.quick.manageConnections', 'Manage {{entity}}', { entity: getEntity('connection').plural }),
      description: t('dashboard.quick.manageConnectionsDesc', 'View {{entity}} {{conns}}', {
        entity: getEntity('employee').singular.toLowerCase(),
        conns: getEntity('connection').plural.toLowerCase(),
      }),
      icon: HiOutlineUserPlus,
      tone: 'brand',
      onClick: () => navigate('/connections'),
    },
  ]

  return (
    <Card padding="md">
      <Card.Header
        title={
          <span className="inline-flex items-center gap-2">
            <HiOutlineBolt className="w-5 h-5 text-ink-muted" />
            {t('dashboard.quick.title', 'Quick actions')}
          </span>
        }
        subtitle={t('dashboard.quick.subtitle', 'Common tasks')}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {actions.map((action) => {
          const Icon = action.icon
          return (
            <button
              key={action.name}
              type="button"
              onClick={action.onClick}
              className="group text-start p-3 rounded-xl border border-border bg-surface hover:border-border-strong hover:shadow-token-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/30 transition-all"
            >
              <div className="flex items-start gap-3">
                <span className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-105 ${TONE_CHIP[action.tone] || TONE_CHIP.brand}`}>
                  <Icon className="w-5 h-5" />
                </span>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold text-ink-primary truncate">{action.name}</h4>
                  <p className="text-xs text-ink-muted mt-0.5 line-clamp-2">{action.description}</p>
                </div>
              </div>
            </button>
          )
        })}
      </div>
    </Card>
  )
}
